import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import { Home, ArrowBack, Search } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";

const NotFoundPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoHome = () => {
    const companyName = localStorage.getItem("companyName");
    if (companyName) {
      navigate(`/${companyName}/dashboard`);
    } else {
      navigate("/");
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            maxWidth: 600,
            width: "100%",
          }}
        >
          {/* 404 Number */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "6rem", sm: "8rem", md: "10rem" },
                fontWeight: 800,
                color: theme.palette.primary.main,
                textShadow: `0 4px 8px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                lineHeight: 0.8,
                mb: 2,
              }}
            >
              404
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Page Not Found
            </Typography>
          </Box>

          {/* Error Message */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                mb: 2,
                fontWeight: 500,
              }}
            >
              Oops! The page you're looking for doesn't exist.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                mb: 1,
              }}
            >
              It might have been moved, deleted, or you entered the wrong URL.
            </Typography>
            {location.pathname && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.disabled,
                  fontFamily: "monospace",
                  backgroundColor: alpha(theme.palette.text.primary, 0.05),
                  padding: "8px 12px",
                  borderRadius: 1,
                  display: "inline-block",
                  mt: 1,
                }}
              >
                {location.pathname}
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                borderColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              Go Back
            </Button>
          </Box>

          {/* Additional Help */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Search fontSize="small" />
              Need help? Try searching or contact support
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default NotFoundPage;
